import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TopMenu } from "../../top-menu/TopMenu";
import { Footer } from "../../footer/Footer";
import { PageHero } from "../../page-hero/PageHero";
import { PROJECTS, CATEGORIES } from "../../../data/projects";

export const Projects = () => {
    const [activeCategory, setActiveCategory] = useState('All');

    const filtered = useMemo(
        () => activeCategory === 'All' ? PROJECTS : PROJECTS.filter(p => p.category === activeCategory),
        [activeCategory]
    );

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <TopMenu />

            <PageHero
                eyebrow="Recent Projects"
                title="Steel"
                accent="we've helped build."
                subtitle="A selection of recent projects across education, commercial, and public works — detailed and modeled in SDS2, delivered to fabricators across the western U.S."
            />

            <main className="flex-1 py-16 md:py-20">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="flex flex-wrap gap-2 mb-10">
                        {CATEGORIES.map((category) => {
                            const isActive = activeCategory === category;
                            return (
                                <button
                                    key={category}
                                    onClick={() => setActiveCategory(category)}
                                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                                        isActive
                                            ? 'bg-navy-900 text-white border-navy-900'
                                            : 'bg-white text-navy-700 border-steel-200 hover:border-navy-600 hover:text-navy-900'
                                    }`}
                                >
                                    {category}
                                    <span className={`ml-2 text-xs ${isActive ? 'text-accent' : 'text-steel-400'}`}>
                                        {category === 'All' ? PROJECTS.length : PROJECTS.filter(p => p.category === category).length}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filtered.map((project) => (
                            <article
                                key={project.slug}
                                className="group relative overflow-hidden rounded-xl bg-navy-900 aspect-[4/3]"
                            >
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-900/40 to-transparent opacity-90 group-hover:opacity-95 transition-opacity" />

                                <div className="absolute top-4 left-4">
                                    <span className="inline-block px-2.5 py-1 rounded-md bg-white/10 backdrop-blur-sm border border-white/20 text-[10px] font-semibold text-white uppercase tracking-wider">
                                        {project.category}
                                    </span>
                                </div>

                                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                    <h3 className="text-lg font-bold font-display mb-1 leading-snug">{project.title}</h3>
                                    <p className="text-sm text-steel-200 m-0">{project.location}</p>
                                </div>
                            </article>
                        ))}
                    </div>

                    {filtered.length === 0 && (
                        <p className="text-center text-steel-500 py-16">No projects in this category yet.</p>
                    )}
                </div>

                <div className="container-px mx-auto max-w-7xl mt-20">
                    <div className="bg-navy-900 rounded-2xl p-10 md:p-14 text-center text-white relative overflow-hidden">
                        <div className="absolute inset-0 bg-hero-radial opacity-50" />
                        <div className="relative">
                            <h2 className="text-display-md font-display mb-4">Have a project in mind?</h2>
                            <p className="text-steel-200 mb-8 max-w-xl mx-auto">
                                Send us your drawings or specs and we'll come back with scope, timeline, and pricing within 48 hours.
                            </p>
                            <Link
                                to="/contact-us"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-signal text-signal-ink font-semibold no-underline hover:bg-signal-hover transition-all shadow-glow-accent hover:scale-[1.02]"
                            >
                                Request a Quote
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};
